import MonetizationOnIcon from "@mui/icons-material/MonetizationOn";
import { ClickAwayListener, IconButton } from "@mui/material";
import React from "react";

import TooltipComponent from "@/main/components/shared/tooltip/tooltipComponent";
import useWindowDimensions from "@/main/hooks/useKogWindowDimension";
import { PartOriginPriceResponse } from "@/main/models/response";
import { ResolutionsEnum } from "@/main/services/enum/ResolutionsEnum";
import { getTranslateByKey, isNullUndefinedEmpty } from "@/main/services/utils";
import colors from "@/main/style/colors";

import * as S from "./styles";

const PART_ORIGIN_INFO_ATTRIBUTE = "data-part-origin-info";

export function isPartOriginInfoTarget(target: EventTarget | null) {
	return target instanceof Element && target.closest(`[${PART_ORIGIN_INFO_ATTRIBUTE}]`) !== null;
}

interface PartOriginInfoProps {
	data?: PartOriginPriceResponse;
	idIconButton?: string;
}

export default function PartOriginInfo({ data, idIconButton = "id-partOriginInfo-iconButton" }: PartOriginInfoProps) {
	const [isOpenTooltip, setIsOpenTooltip] = React.useState(false);
	const { width } = useWindowDimensions();

	const isMobile = width < ResolutionsEnum.desktop;
	const hasDate = !isNullUndefinedEmpty(data?.originalCreatedDateFormatted);
	const hasPrice = !isNullUndefinedEmpty(data?.originalProcurementPriceFormatted);

	if (!data?.showOriginIcon || (!hasDate && !hasPrice)) return null;

	function renderTooltipContent() {
		return (
			<S.TooltipContent>
				<S.TooltipTitle>{getTranslateByKey("information.initialAssignment")}</S.TooltipTitle>

				<S.TooltipRow>
					<S.TooltipLabel>{getTranslateByKey("information.date")}</S.TooltipLabel>
					<S.TooltipValue translate="no">{hasDate ? data?.originalCreatedDateFormatted : "-"}</S.TooltipValue>
				</S.TooltipRow>

				{hasPrice && (
					<S.TooltipRow>
						<S.TooltipLabel>{getTranslateByKey("information.originPrice")}</S.TooltipLabel>
						<S.TooltipValue translate="no">{data?.originalProcurementPriceFormatted}</S.TooltipValue>
					</S.TooltipRow>
				)}
			</S.TooltipContent>
		);
	}

	return (
		<ClickAwayListener onClickAway={() => setIsOpenTooltip(false)}>
			<div {...{ [PART_ORIGIN_INFO_ATTRIBUTE]: true }}>
				<TooltipComponent
					open={isOpenTooltip}
					maxWidth="100%"
					placement="top"
					title={renderTooltipContent()}
					onOpen={() => setIsOpenTooltip(true)}
					onClose={() => setIsOpenTooltip(false)}
					disableFocusListener
					disableTouchListener
					disableInteractive
					disableHoverListener={isMobile}
				>
					<IconButton
						id={idIconButton}
						size="small"
						onClick={(e) => {
							e.stopPropagation();
							e.preventDefault();
							if (isMobile) setIsOpenTooltip((prev) => !prev);
						}}
					>
						<MonetizationOnIcon style={{ color: colors.primaryBlueDark }} fontSize="small" />
					</IconButton>
				</TooltipComponent>
			</div>
		</ClickAwayListener>
	);
}
